import { useMemo } from 'react';
import 'twin.macro';

import { FaceFrownIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import Snackbar, { SnackbarVariants } from '@/components/Snackbar';
import IntervieweeCardContainer from '@/containers/IntervieweeCardContainer';
import QuestionSnapshotContainer from '@/containers/QuestionSnapshotContainer';
import {
  useGetQuestionSnapshotBySessionIdWithTokenQuery,
  useGetSessionWithTokenQuery,
} from '@/libs/graphql';
import { convertIntervieweeInfo } from '@/utils/convertIntervieweeInfo';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const ErrorView = ({
  message = 'ไม่สามารถดูการสัมภาษณ์ได้',
  code,
}: {
  message: string;
  code?: string;
}) => {
  return (
    <div tw="flex h-full w-full flex-col items-center justify-center gap-2 rounded-2xl border bg-white p-14">
      <FaceFrownIcon tw="h-6 w-6" />
      <h5>{message}</h5>
      {code && <h5>[{code}]</h5>}
    </div>
  );
};

const IntervieweeView = () => {
  const params = useParams();
  const searchParams = useQuery();
  const { t } = useTranslation();
  const sessionId = params.intervieweeId ?? '';
  const token = searchParams.get('token');

  if (!token) {
    return <ErrorView message="ไม่สามารถดูการสัมภาษณ์ได้" code="E001" />;
  }

  const {
    data: sessionData,
    loading: sessionLoading,
    error: sessionError,
  } = useGetSessionWithTokenQuery({
    variables: { sessionId, token },
    fetchPolicy: 'network-only',
  });

  const intervieweeDetails = sessionData?.getSessionWithToken;

  const { data: questionSnapshotData, loading: questionSnapshotLoading } =
    useGetQuestionSnapshotBySessionIdWithTokenQuery({
      variables: { sessionId, token },
      fetchPolicy: 'network-only',
      skip: !sessionId,
    });

  if (sessionError?.message === 'Invalid token') {
    return <ErrorView message="ไม่สามารถดูการสัมภาษณ์ได้" code="E002" />;
  }

  if (sessionError?.message === 'Token is expired') {
    return <ErrorView message="ลิงก์ของคุณหมดอายุแล้ว" />;
  }

  const questionSnapshot = questionSnapshotData?.getQuestionSnapshotBySessionIdWithToken ?? [];

  const warningBox = t('sessionViewer.noticeBox.label');
  const warningBoxLevel = t('sessionViewer.noticeBox.variant') as SnackbarVariants;

  const intervieweeInfo = convertIntervieweeInfo(intervieweeDetails);

  return (
    <div tw="m-auto flex min-h-screen flex-col space-y-5 px-4 py-6 lg:(px-20 py-14)">
      <h3>รายละเอียดการสัมภาษณ์</h3>
      <div tw="sticky top-0 z-20">
        <Snackbar text={warningBox} variant={warningBoxLevel} />
      </div>
      <div tw="flex flex-col gap-2">
        <h5>ผู้สมัคร</h5>
        <IntervieweeCardContainer
          viewer
          isSessionDataLoading={sessionLoading}
          {...intervieweeInfo}
        />
      </div>
      <QuestionSnapshotContainer
        position={intervieweeInfo.position}
        questionSnapshot={questionSnapshot}
        questionSnapshotLoading={questionSnapshotLoading}
      />
    </div>
  );
};

export default IntervieweeView;
